import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { UserContext } from '../userContext';
import { FaSearch, FaPlay, FaFilePdf, FaImage, FaCalendarAlt, FaUser, FaEye, FaUserGraduate, FaArrowLeft, FaCheckCircle } from 'react-icons/fa';
import BotaoNaoEncontrou from '../components/BotaoNaoEncontrou';
import './ResultadosBusca.css';
import { normalizeText } from '../utils/text';

function ResultadosBusca() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(UserContext);

  const urlParams = new URLSearchParams(location.search);
  const termoBusca = urlParams.get('q') || '';

  const [materiais, setMateriais] = useState([]);
  const [loading, setLoading] = useState(true);
  const [novoTermoBusca, setNovoTermoBusca] = useState(termoBusca);

  useEffect(() => {
    if (termoBusca) {
      buscarMateriais(termoBusca);
    } else {
      setLoading(false);
    }
  }, [termoBusca]);

  const buscarMateriais = async (termo) => {
    if (!termo.trim()) {
      setMateriais([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    console.log('🔍 Buscando por:', termo);

    try {
      const termoNormalizado = normalizeText(termo);

      const materialsRef = collection(db, 'materials');
      const materialsSnapshot = await getDocs(materialsRef);
      const materialsData = materialsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        source: 'admin'
      }));

      const materiaisFiltradosAdmin = materialsData.filter(material => {
        const title = normalizeText(material.title || '');
        const description = normalizeText(material.description || '');
        const authorName = normalizeText(material.authorName || '');
        const subcategory = normalizeText(material.subcategory || '');
        const keywords = normalizeText((material.keywords || []).join(' '));

        return title.includes(termoNormalizado) ||
          description.includes(termoNormalizado) ||
          authorName.includes(termoNormalizado) ||
          subcategory.includes(termoNormalizado) ||
          keywords.includes(termoNormalizado);
      });

      console.log('📚 Materiais admin encontrados:', materiaisFiltradosAdmin.length);

      const studentMaterialsRef = collection(db, 'student_materials');
      const studentSnapshot = await getDocs(studentMaterialsRef);
      const studentMaterials = studentSnapshot.docs
        .filter(doc => doc.data().status === 'aprovado')
        .map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            title: data.titulo,
            description: data.descricao,
            materialType: data.materialType,
            materialUrl: data.arquivoURL || data.videoUrl,
            coverImage: data.coverImage || data.arquivoURL || data.videoUrl,
            authorName: data.authorName,
            createdAt: data.createdAt,
            subcategory: data.subcategoria,
            category: data.categoria,
            source: 'student',
            conteudo: data.conteudo,
            resposta: data.resposta,
            videoResposta: data.videoResposta,
            hasThumbnail: data.hasThumbnail,
            semana: data.semana || data.curso || ''
          };
        });

      const materiaisFiltradosAlunos = studentMaterials.filter(material => {
        const title = normalizeText(material.title || '');
        const description = normalizeText(material.description || '');
        const authorName = normalizeText(material.authorName || '');
        const subcategory = normalizeText(material.subcategory || '');
        const conteudo = normalizeText(material.conteudo || '');

        return title.includes(termoNormalizado) ||
          description.includes(termoNormalizado) ||
          authorName.includes(termoNormalizado) ||
          subcategory.includes(termoNormalizado) ||
          conteudo.includes(termoNormalizado);
      });

      console.log('👨‍🎓 Materiais de alunos encontrados:', materiaisFiltradosAlunos.length);

      const todosMateriais = [...materiaisFiltradosAdmin, ...materiaisFiltradosAlunos];
      todosMateriais.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
        return dateB - dateA;
      });

      console.log('📦 Total de resultados:', todosMateriais.length);
      setMateriais(todosMateriais);
    } catch (error) {
      console.error('❌ Erro ao buscar materiais:', error);
      setMateriais([]);
    } finally {
      setLoading(false);
    }
  };

  const handleNovaBusca = (e) => {
    e.preventDefault();
    if (novoTermoBusca.trim()) {
      navigate(`/busca?q=${encodeURIComponent(novoTermoBusca.trim())}`);
    }
  };

  const handleVerConteudo = (material) => {
    navigate(`/conteudo/${material.id}`);
  };

  const getStaticPreview = (material) => {
    const materialType = material.materialType?.toUpperCase();
    const materialUrl = material.materialUrl;

    if (material.coverImage && material.coverImage !== materialUrl && material.hasThumbnail) {
      return material.coverImage;
    }

    if (material.coverImage && material.coverImage !== materialUrl) {
      return material.coverImage;
    }

    if (materialType === 'YOUTUBE' && materialUrl) {
      const videoId = materialUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/)?.[1];
      return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null;
    }

    if (materialType === 'VIMEO' && materialUrl) {
      const vimeoId = materialUrl.match(/vimeo\.com\/(\d+)/)?.[1];
      return vimeoId ? `https://i.vimeocdn.com/video/${vimeoId}_640x360.jpg` : null;
    }

    if (['PNG', 'JPG', 'JPEG'].includes(materialType) && materialUrl) {
      return materialUrl;
    }

    if (['PDF'].includes(materialType)) {
      return null;
    }

    return null;
  };

  const getCoverImage = (material) => {
    return getStaticPreview(material);
  };

  const getTypeIcon = (materialType) => {
    const type = materialType?.toUpperCase();
    switch (type) {
      case 'YOUTUBE':
      case 'VIMEO':
        return <FaPlay />;
      case 'PDF':
        return <FaFilePdf />;
      case 'PNG':
      case 'JPG':
      case 'JPEG':
        return <FaImage />;
      default:
        return <FaEye />;
    }
  };

  const getTypeBadgeClass = (materialType) => {
    const type = materialType?.toUpperCase();
    switch (type) {
      case 'YOUTUBE':
      case 'VIMEO':
        return 'type-video';
      case 'PDF':
        return 'type-pdf';
      case 'PNG':
      case 'JPG':
      case 'JPEG':
        return 'type-image';
      default:
        return 'type-default';
    }
  };

  const formatDate = (dateData) => {
    if (!dateData) return 'Data não disponível';

    let date;
    if (dateData.seconds) {
      date = new Date(dateData.seconds * 1000);
    } else if (dateData.toDate) {
      date = dateData.toDate();
    } else if (typeof dateData === 'string') {
      date = new Date(dateData);
    } else {
      date = new Date(dateData);
    }

    return date.toLocaleDateString('pt-BR');
  };

  const getSemanaLabel = (material) => {
    const rawSemana = material?.semana || material?.curso;
    if (!rawSemana) return '';

    const semana = String(rawSemana).trim();
    if (!semana) return '';

    const match = semana.match(/semana\s*(\d+)/i);
    return match ? `Semana ${match[1]}` : semana;
  };

  const highlightText = (text, searchTerm) => {
    if (!text || !searchTerm) return text;

    const fullText = String(text);
    const term = String(searchTerm).trim();
    if (!term) return fullText;

    const lowerText = fullText.toLowerCase();
    const lowerTerm = term.toLowerCase();
    const chunks = [];
    let cursor = 0;
    let key = 0;

    while (true) {
      const matchIndex = lowerText.indexOf(lowerTerm, cursor);
      if (matchIndex === -1) {
        if (cursor < fullText.length) {
          chunks.push(fullText.slice(cursor));
        }
        break;
      }

      if (matchIndex > cursor) {
        chunks.push(fullText.slice(cursor, matchIndex));
      }

      const endIndex = matchIndex + term.length;
      chunks.push(
        <mark key={`highlight-${key}`} className="highlight">
          {fullText.slice(matchIndex, endIndex)}
        </mark>
      );

      cursor = endIndex;
      key += 1;
    }

    return chunks;
  };

  return (
    <div className="resultados-busca-page">
      <header className="busca-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <FaArrowLeft /> Voltar
        </button>

        <div className="busca-info">
          <h1 className="busca-titulo">Resultados da Busca</h1>
          {termoBusca && (
            <p className="busca-subtitulo">
              {loading ? 'Buscando' : `${materiais.length} resultados encontrados`} para "{termoBusca}"
            </p>
          )}
        </div>
      </header>

      <div className="nova-busca-container">
        <form onSubmit={handleNovaBusca} className="nova-busca-form">
          <div className="search-container">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Digite sua busca..."
              value={novoTermoBusca}
              onChange={(e) => setNovoTermoBusca(e.target.value)}
              autoFocus={!termoBusca}
            />
            <button type="submit" className="search-button">
              Buscar
            </button>
          </div>
        </form>
      </div>

      <div className="resultados-content">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Buscando materiais...</p>
          </div>
        ) : !termoBusca ? (
          <div className="empty-state">
            <div className="empty-icon">
              <FaSearch />
            </div>
            <h3>Faça uma busca</h3>
            <p>Digite um termo no campo acima para buscar por materiais, autores ou temas.</p>
          </div>
        ) : materiais.length > 0 ? (
          <div className="materiais-grid">
            {materiais.map((mat) => {
              const coverImage = getCoverImage(mat);
              const materialType = mat.materialType?.toUpperCase();
              const hasThumbnailGenerated = mat.hasThumbnail;
              const semanaLabel = getSemanaLabel(mat);

              return (
                <div className="material-card" key={`${mat.source}-${mat.id}`}>
                  <div className="cover-wrapper">
                    {coverImage ? (
                      <img
                        src={coverImage}
                        alt={mat.title}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const placeholder = e.target.nextSibling;
                          if (placeholder) placeholder.style.display = 'flex';
                        }}
                      />
                    ) : null}

                    <div className={`cover-placeholder ${getTypeBadgeClass(materialType)}`}
                      style={{ display: coverImage ? 'none' : 'flex' }}>
                      <div className="placeholder-icon">
                        {getTypeIcon(materialType)}
                      </div>
                      <div className="placeholder-text">
                        <span className="material-type">{materialType || 'CONTEÚDO'}</span>
                        <span className="material-title">{mat.title}</span>
                      </div>
                    </div>

                    <div className={`type-badge ${getTypeBadgeClass(materialType)}`}>
                      {getTypeIcon(materialType)}
                      <span>{materialType}</span>
                    </div>

                    {materialType === 'PDF' && hasThumbnailGenerated && (
                      <div style={{
                        position: 'absolute',
                        display: 'flex',
                        top: '0.5rem',
                        right: '0.5rem',
                        background: 'rgba(40, 167, 69, 0.9)',
                        color: 'white',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '12px',
                        fontSize: '0.6rem',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        zIndex: 2
                      }}>
                        Preview
                      </div>
                    )}

                    {mat.source === 'student' ? (
                      <div className="student-badge">
                        <FaUserGraduate />
                        <span>Material do Aluno</span>
                      </div>
                    ) : (
                      <div className="expert-badge">
                        <FaCheckCircle />
                        <span>Especialista</span>
                      </div>
                    )}

                    <div className="hover-overlay">
                      <button
                        className="preview-button"
                        onClick={() => handleVerConteudo(mat)}
                      >
                        <FaEye />
                        Ver Conteúdo
                      </button>
                    </div>
                  </div>

                  <div className="material-info">
                    <h2 className="material-title">
                      {highlightText(mat.title, termoBusca)}
                    </h2>
                    {semanaLabel && (
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '0.3rem 0.8rem',
                          borderRadius: '16px',
                          fontSize: '0.7rem',
                          fontWeight: '700',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          background: 'rgba(250, 204, 21, 0.95)',
                          color: '#111827',
                          border: '1px solid rgba(250, 204, 21, 0.55)',
                          boxShadow: '0 2px 8px rgba(250, 204, 21, 0.2)',
                          width: 'fit-content'
                        }}
                        title={`Conteudo referente a ${semanaLabel}`}
                      >
                        <span>{semanaLabel}</span>
                      </div>
                    )}

                    <div className="material-meta">
                      <div className="meta-item">
                        <FaUser className="meta-icon" />
                        <span>
                          {highlightText(mat.authorName || 'Conecta Admin', termoBusca)}
                        </span>
                        {mat.source === 'student' ? (
                          <span className="author-type">(Aluno)</span>
                        ) : mat.source === 'admin' ? (
                          <span className="author-type-expert">
                            <span className="dot"></span>
                            (Especialista)
                          </span>
                        ) : null}
                      </div>

                      <div className="meta-item">
                        <FaCalendarAlt className="meta-icon" />
                        <span>{formatDate(mat.createdAt)}</span>
                      </div>

                      {mat.subcategory && (
                        <div className="meta-item">
                          <span className="subcategory-tag">
                            {highlightText(mat.subcategory, termoBusca)}
                          </span>
                        </div>
                      )}
                    </div>

                    <p className="material-description">
                      {mat.description ?
                        highlightText(
                          mat.description.length > 120
                            ? `${mat.description.substring(0, 120)}...`
                            : mat.description,
                          termoBusca
                        ) : 'Sem descrição disponível'
                      }
                    </p>

                    <button
                      className="ver-conteudo-button"
                      onClick={() => handleVerConteudo(mat)}
                    >
                      <FaEye />
                      Ver Conteúdo
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">
              <FaSearch />
            </div>
            <h3>Nenhum resultado encontrado</h3>
            <p>
              Não encontramos materiais relacionados a "{termoBusca}".
            </p>
            <div className="suggestions">
              <p><strong>Dicas para melhorar sua busca:</strong></p>
              <ul>
                <li>Verifique a ortografia das palavras</li>
                <li>Tente palavras mais gerais</li>
                <li>Use sinônimos ou termos relacionados</li>
                <li>Remova filtros muito específicos</li>
              </ul>
            </div>

            <BotaoNaoEncontrou termoBusca={termoBusca} />
          </div>
        )}
      </div>
    </div>
  );
}

export default ResultadosBusca;
